import { FC } from "react";
import { DocumentFormValues } from "../../Logic";
import { 
    AppEnum, 
    FaFileInvoiceDollar, 
    FormikFieldSetter, 
    InputField, 
    SelectField 
} from "@/Shared";

interface KnownSourceDocFormProps<T> {
    scrollParent : HTMLElement | null;
    isModalMode: boolean;
    options: AppEnum[];
}

/**
 * Component: KnownSourceDocForm
 *
 * Description:
 * This component handles document creation when the document
 * is attached to an already known business resource
 * (e.g. Lease, Mandate, Property).
 *
 * It adapts its behavior based on the rendering context:
 * - Modal mode   → the resource is implicit and already selected
 * - Page mode    → the user must explicitly select the resource
 *
 * Responsibilities:
 * - Display the resource selector or read-only field
 * - Ensure the document is correctly bound to its source
 * - Remain focused on UI concerns only
 *
 * Design principles:
 * - Context-aware UI (modal vs page)
 * - No business decision logic
 * - Delegation to Formik for state handling
 *
 * Author: Samano CASTRE
 */
const KnownSourceDocForm: FC<KnownSourceDocFormProps<any>> = ({ 
    scrollParent,
    options, 
    isModalMode = false 
}) => {

    /**
	 * Renders the resource binding section.
	 *
	 * - In modal mode:
	 *   The resource context is implicit (user is already on the resource),
	 *   so the resource ID is displayed as read-only.
	 *
	 * - In page mode:
	 *   The user must explicitly select the target resource
	 *   from the available options.
	 */
    return (
        <fieldset className=''>
            <legend>Sources de données</legend>
            {
                isModalMode 
                ? (
                <InputField
                    label='Resource N°'
                    required
                    icon={<FaFileInvoiceDollar />}
                    name='resourceId'
                    readOnly />
                )
                :(
                <SelectField
                    label='Selectionner la resource'
                    required
                    icon={<FaFileInvoiceDollar />}
                    name='resourceId'
                    options={options} 
                    scrollParent={scrollParent}
                    />
                )
            }
        </fieldset>
    );
}

export default KnownSourceDocForm;